import { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Step, StepLabel, Stepper } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import {
  Estate_FieldsFragment,
  EstateImport,
  EstateType,
  useEstate_GetEstateByIdQuery,
} from 'queries';

import Spinner from 'common/spinner';
import { EstateDashboardTab } from 'pages/estate-dashboard/estate-dashboard';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

import EstateDetailForm from './estate-detail-form';
import EstateDocumentsForm from './estate-documents-form';
import EstateGetStartedForm from './estate-get-started-form';

type Props = {
  estateId?: string;
  estateImport?: EstateImport | null;
  onExitClick: () => void;
  onOrderSearchButtonClick?: () => void;
};

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      width: '100%',
      marginBottom: '20px',
    },
    stepper: {
      paddingBottom: '0px',
    },
    stepContentWrapper: {
      marginTop: '20px',
      marginBottom: '20px',
      backgroundColor: 'rgba(240, 243, 246, 0.7)',
      padding: '25px',
      paddingTop: '20px',
      '& input': {
        background: 'white',
      },
    },
  }),
);
const steps = ['Get Started', 'Details', 'Documents'];

const EstateStepper = ({
  estateId,
  estateImport,
  onExitClick,
  onOrderSearchButtonClick,
}: Props) => {
  const classes = useStyles();
  const history = useHistory();
  const { portalId } = useParams<Record<string, string>>();
  const [activeStep, setActiveStep] = useState(estateId ? 1 : 0);
  const [estateType, setEstateType] = useState<EstateType>();
  const [editingEstateId, setEditingEstateId] = useState(estateId);

  const { loading: estateLoading, data: estateData } = useEstate_GetEstateByIdQuery({
    variables: { id: editingEstateId || '' },
    skip: !editingEstateId,
    onError: (e) => {
      showAlert({
        message: 'Error getting estate by ID: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const estate = estateData?.data;

  if (estateLoading && !estate) {
    return <Spinner />;
  }

  const handleBack = () => {
    if (activeStep <= 0 || (activeStep === 1 && estate?.id)) {
      onExitClick();
    }

    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleNext = () => {
    if (activeStep === steps.length - 1 && estate?.id) {
      history.push(
        `/home/${portalId}/estate/${estate.id}/estate-dashboard?tab=${EstateDashboardTab.ESTATE_CHECKLIST}`,
      );
      history.go(0); // Force the page to re-render.
      onExitClick();
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const onSubmitGetStartedForm = (selectedType: EstateType) => {
    handleNext();
    setEstateType(selectedType);
  };

  const onSubmitEstateDetailForm = (estate: Estate_FieldsFragment) => {
    setEditingEstateId(estate.id);
    handleNext();
  };

  const getStepComponent = (stepIndex: number) => {
    if (stepIndex === 0) {
      return <EstateGetStartedForm onExitClick={onExitClick} onSubmit={onSubmitGetStartedForm} />;
    }

    const editingEstateType = estate?.type || estateType;

    if (stepIndex === 1 && editingEstateType) {
      return (
        <EstateDetailForm
          estateImport={estateImport}
          handleBack={handleBack}
          onSubmit={onSubmitEstateDetailForm}
          estateType={editingEstateType}
          editingEstate={estate}
          isEditing={!!estateId}
        />
      );
    }

    if (stepIndex === 2 && estate) {
      return (
        <EstateDocumentsForm
          handleBack={handleBack}
          handleNext={handleNext}
          estate={estate}
          onOrderSearchButtonClick={onOrderSearchButtonClick}
        />
      );
    }
  };

  return (
    <div className={classes.root}>
      <Stepper activeStep={activeStep} alternativeLabel className={classes.stepper}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <div className={classes.stepContentWrapper}>{getStepComponent(activeStep)}</div>
    </div>
  );
};

export default EstateStepper;
